import { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, Plus, Edit, Trash2, Copy } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    certification_type: '',
    subject: '',
    body: '',
    is_default: false
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/email-templates`);
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      certification_type: '',
      subject: '',
      body: '',
      is_default: false
    });
    setShowDialog(true);
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      certification_type: template.certification_type,
      subject: template.subject,
      body: template.body,
      is_default: template.is_default
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      
      if (editingTemplate) {
        await axios.patch(`${API}/email-templates/${editingTemplate.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Template updated successfully');
      } else {
        await axios.post(`${API}/email-templates`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Template created successfully');
      }
      
      setShowDialog(false);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/email-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-4xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Email Templates
          </h2>
          <p className="text-slate-600 dark:text-slate-400">Manage distribution email templates for different certifications</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-gradient-to-r from-blue-500 to-cyan-400">
          <Plus className="w-4 h-4 mr-2" />
          Create Template
        </Button>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
        <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Available Template Variables:</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-blue-800 dark:text-blue-200">
          <code>{'{{student_name}}'}</code>
          <code>{'{{voucher_code}}'}</code>
          <code>{'{{certification_type}}'}</code>
          <code>{'{{due_date}}'}</code>
          <code>{'{{expiry_date}}'}</code>
          <code>{'{{student_email}}'}</code>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {templates.map((template) => (
            <Card key={template.id} className="border-slate-200 dark:border-slate-700">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="w-5 h-5" />
                      {template.name}
                      {template.is_default && (
                        <Badge variant="outline" className="ml-2 bg-green-50 text-green-700 border-green-300">Default</Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="mt-1">{template.certification_type}</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEditDialog(template)}>
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(template.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Subject:</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 p-2 rounded">
                      {template.subject}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Body Preview:</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 p-2 rounded max-h-32 overflow-y-auto whitespace-pre-wrap">
                      {template.body.substring(0, 200)}...
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit' : 'Create'} Email Template</DialogTitle>
            <DialogDescription>
              {editingTemplate ? 'Update the' : 'Create a new'} email template for voucher distribution
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Template Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., CompTIA Security+"
                />
              </div>
              <div>
                <Label htmlFor="cert_type">Certification Type *</Label>
                <Input
                  id="cert_type"
                  value={formData.certification_type}
                  onChange={(e) => setFormData({ ...formData, certification_type: e.target.value })}
                  required
                  placeholder="e.g., CompTIA Security+"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="subject">Email Subject *</Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
                placeholder="Your {{certification_type}} Voucher"
              />
            </div>

            <div>
              <Label htmlFor="body">Email Body *</Label>
              <textarea
                id="body"
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                required
                rows={15}
                className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 font-mono text-sm"
                placeholder="Dear {{student_name}},&#10;&#10;Your voucher code is: {{voucher_code}}..."
              />
              <p className="text-xs text-slate-500 mt-1">Use template variables like {'{{student_name}}'}, {'{{voucher_code}}'}, etc.</p>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_default"
                checked={formData.is_default}
                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                className="rounded"
              />
              <label htmlFor="is_default" className="text-sm cursor-pointer">
                Set as default template (used when no specific template exists)
              </label>
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-400">
                {editingTemplate ? 'Update' : 'Create'} Template
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplates;
